/**
 * Traditional Chinese (Taiwan) message catalog — mirrors the shape of
 * `messages.en.ts` exactly; `satisfies MessagesShape` makes a missing or
 * renamed key a TypeScript error. Register: 你 (no T–V lock).
 *
 * NOT a hanzi conversion of zh-CN — Taiwan vocabulary diverges: 設定
 * (settings), 訊息 (message), 傳送 (send), 回覆 (reply), 搜尋 (search),
 * 儲存 (save), 伺服器 (server), 推播 (push), 剪貼簿 (clipboard), 權杖
 * (token), 裝置 (device), 擴充功能 (extension), 端對端 (E2E), 最佳化
 * (optimization), 「」 corner quotes. "Glance" untranslated (D1);
 * "剪貼項目" renders the D2 clip noun (TW reading of the zh-CN 剪贴项
 * proposal).
 *
 * Plural notes: other-only CLDR — measure words (則/部/個) carry the
 * number; every `selectPlural("zh-TW", …)` supplies only `other`.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core/i18n";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "已連線",
    statusReconnecting: "正在重新連線…",
    statusDisconnected: "已中斷連線",
    openSettings: "開啟設定",
    pairedFallbackDevice: "你的手機",
    publicModeToggleEnable: "隱藏通知內容",
    publicModeToggleDisable: "顯示通知內容",
  },

  notifications: {
    replyAriaLabel: "回覆",
    replyPlaceholder: "回覆…",
    replySend: "傳送",
    sent: "已傳送",
    failedToSend: "傳送失敗",
    unread: "未讀",
    dismiss: "清除",
    notificationImageAlt: "通知圖片",
  },

  conversations: {
    replyAriaLabel: "回覆",
    replyPlaceholder: "回覆…",
    replySend: "傳送",
    sent: "已傳送",
    failedToSend: "傳送失敗",
    dismiss: "清除對話",
    messagesListLabel: "此對話中的訊息",
    deviceOwner: "我",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "沒有未讀訊息"
        : `${count} ${selectPlural("zh-TW", count, { other: "則未讀訊息" })}`,
  },

  list: {
    today: "今天",
    yesterday: "昨天",
    searchNotifications: "搜尋通知",
    clearSearch: "清除搜尋",
    clearAll: "全部清除",
    markAllRead: "全部標為已讀",
    groupCheckboxLabel: "分組顯示",
    noNotificationsTitle: "還沒有通知",
    noNotificationsDesc: "從手機鏡射過來的新訊息會顯示在這裡。",
    noMatchesTitle: "沒有符合的結果",
    notificationsList: "通知",
    privacyAppList: "依應用程式檢視通知",
    clear: "清除",
    clearMatches: (count: number): string =>
      `清除 ${count} ${selectPlural("zh-TW", count, { other: "筆符合結果" })}`,
    noMatchesDesc: (query: string): string => `沒有符合「${query}」的通知。`,
    appNotificationsGroup: (appName: string): string => `${appName} 的通知`,
    dismissAllFrom: (appName: string): string => `清除 ${appName} 的全部通知`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("zh-TW", count, { other: "則訊息" })
          : kind === "calls"
            ? selectPlural("zh-TW", count, { other: "通來電" })
            : selectPlural("zh-TW", count, { other: "則通知" });
      return `${count} ${noun}`;
    },
    kbdDismiss: "清除",
    kbdNavigate: "切換",
    kbdOrSeparator: "或",
  },

  settings: {
    back: "返回通知清單",
    settings: "設定",
    general: "一般",
    theme: "主題",
    themeAuto: "自動",
    themeLight: "淺色",
    themeDark: "深色",
    language: "語言",
    languageSystemDefault: "系統預設",
    notificationsSection: "通知",
    osNotifications: "系統通知",
    callNotifications: "來電通知",
    privacySecurity: "隱私",
    analyticsToggle: "匿名使用統計",
    analyticsToggleDescription: "僅計數與里程碑——絕不包含你的通知。",
    statusConnected: "已連線",
    statusReconnecting: "正在重新連線…",
    statusDisconnected: "已中斷連線",
    disconnectFromPhone: "中斷與手機的連線",
    disconnectConfirm: "確定要中斷嗎？裝置將解除配對。",
    yesDisconnect: "確定中斷",
    cancel: "取消",
    version: "版本",
    encryption: "加密",
    encryptionValue: "E2E (NaCl)",
    needHelp: "需要協助？寄信到 __EMAIL__",
    disconnectFailed: "中斷連線失敗——請再試一次",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `已中斷連線，但本機清理失敗：${errorMsg}。請重新整理頁面。`,
    disconnectLocalCleanupFailed: "已中斷連線，但本機清理失敗。請重新整理頁面。",
  },

  pairing: {
    enterCodeInstruction: "在 Reflecto 應用程式中輸入這組配對碼",
    pairedSuccessfully: "配對成功！",
    pairingFailed: "配對失敗",
    requestingPairingCode: "正在要求配對碼…",
    codeExpired: "配對碼已過期",
    tryAgain: "再試一次",
    failedToStartPairing: "無法開始配對",
    installHeading: "還沒安裝應用程式嗎？",
    installScanHint: "用手機相機掃描",
    installQrLabel: "用來在手機上安裝 Reflecto 的 QR 碼",
    capabilitiesHeading: "配對後你可以：",
    capabilityMirror: "在這裡查看手機通知",
    capabilityReply: "不用拿起手機就能回覆",
    capabilityShare: "雙向分享剪貼簿和檔案",
    encryptionNote: "端對端加密，伺服器永遠看不到你的內容。",
  },

  dnd: {
    title: "請勿打擾",
    description: "在接下來的 1 小時內暫停此裝置上的通知。",
    snooze1h: "暫停 1 小時",
    snoozeActiveUntil: (formattedTime: string): string => `生效至 ${formattedTime}`,
    cancelSnooze: "取消",
  },

  connectors: {
    title: "已連結的服務",
    emptyBody:
      "還沒有已連結的服務。在 ChatGPT 或 Claude 的連接器設定中發起連結，然後在手機上核准配對碼。",
    scopeSend: "可以傳送通知",
    added: (date: string): string => `新增於 ${date}`,
    lastUsed: (relative: string): string => `上次使用 ${relative}`,
    neverUsed: "從未使用",
    revoke: "中斷連結",
    revokeConfirm: (label: string): string => `要中斷 ${label} 嗎？它會立即失去存取權。`,
    revokeAction: "中斷連結",
    revokeConfirmLabel: (label: string): string => `中斷 ${label}`,
    cancel: "取消",
    loadError: "無法載入已連結的服務。請再試一次。",
    revokeError: "無法中斷該服務。請再試一次。",
    retry: "重試",
  },

  safetyNumber: {
    rowLabel: "驗證加密",
    rowSublabel: "與您的手機比較安全碼",
    cardTitle: "安全碼",
    caption: (deviceLabel: string): string =>
      `與 ${deviceLabel} 上顯示的號碼進行比對。如果兩者都相符，說明您的端對端加密保持完整。`,
    repairNote: "重新配對裝置時，此號碼會改變。",
    copy: "複製",
    copied: "已複製",
    maskedHint: "隱私模式啟用時隱藏",
  },

  feedback: {
    title: "提供意見回饋",
    close: "關閉",
    messageLabel: "意見內容",
    messagePlaceholder: "發現了 Bug、有想法，或覺得哪裡怪怪的？告訴我們。",
    categoryLabel: "類別",
    categoryBug: "Bug",
    categoryIdea: "想法",
    categoryOther: "其他",
    emailLabel: "電子郵件（選填，只在需要回覆時填寫）",
    emailPlaceholder: "you@example.com",
    emailHelp: "我們不會把你加入任何郵寄清單。",
    disclosure:
      "你的意見會傳送到 Reflecto 伺服器，並記錄在我們的內部問題追蹤系統。為了關聯報告，我們會附上應用程式版本和配對時指派的裝置 ID。我們看不到你的通知、聯絡人或任何個人資料。電子郵件為選填——只在需要回覆時填寫。我們不會把你加入任何郵寄清單。",
    submit: "提供意見回饋",
    sending: "正在傳送…",
    success: "謝謝——會有真人閱讀，通常在 24 小時內",
    error: "傳送失敗。請再試一次。",
    done: "完成",
    sendFeedback: "提供意見回饋",
  },

  uninstall: {
    headline: "Reflecto 擴充功能已解除安裝",
    body: "從來就沒有帳號，擴充功能也只把配對資訊保存在這台電腦上，所以沒有什麼需要你清理的。",
    ask: "如果你有三十秒，我們想知道哪裡出了問題。",
    categoryLegend: "這是哪一類意見？",
    categoryBug: "有功能壞了",
    categoryIdea: "少了我需要的功能",
    categoryOther: "其他情況",
    messageLabel: "哪裡出了問題？",
    messagePlaceholder: "過了幾個小時後就收不到通知了。",
    messageRequired: "先寫一句話再傳送。",
    charactersLeft: (count: number): string => `還剩 ${count} 個字元`,
    emailLabel: "電子郵件（選填）",
    emailPlaceholder: "you@example.com",
    emailHelp: "只用來回覆你，不作他用。",
    submit: "提供意見回饋",
    sending: "正在傳送…",
    retry: "再試一次",
    privacyNote:
      "這份表單不與任何裝置關聯，不包含你的通知、剪貼簿或檔案。電子郵件為選填，只用於回覆。",
    success: "謝謝。每一則都會有真人閱讀。",
    errorGeneric: "沒有傳送成功。你寫的內容還在。",
    errorRateLimited: "你最近傳送了多則訊息。請大約一小時後再試。你寫的內容會保留在這裡。",
    footerWebApp: "沒有擴充功能，Reflecto 網頁應用程式仍然可用。",
    footerWebAppLink: "開啟網頁應用程式",
  },

  glance: {
    // Brand term (glossary D1): the feature name "Glance" is the heading
    // itself in non-English locales — the EN "At a glance" wordplay is
    // EN-only.
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "關閉",
    aiSummaryTag: "AI 摘要",
    errorAi: "目前無法產生摘要。",
    retryCta: "再試一次",
    downloadHeading: "取得 AI 摘要",
    downloadBody: "用裝置端 AI 摘要快速掌握錯過的通知。全部都在你的裝置上執行。",
    downloadCta: "下載模型",
    downloadPrivacyNote: "只需下載一次。伺服器永遠看不到你的通知。",
    downloadingHeading: "正在下載模型…",
    caughtUp: "都看完了——目前沒有需要你處理的事。",
    needsYou: "需要處理",
    quiet: "其他",
    scanning: (pending: number): string => `正在摘要 ${pending} 則…`,
    jump: "開啟通知",
    affordanceReply: "回覆",
    affordanceOpen: "開啟",
    affordanceCopyOtp: "複製驗證碼",
    buttonLabel: "Glance",
  },

  update: {
    title: "有可用的更新",
    description: "新版 Reflecto 已準備就緒。重新整理即可更新。",
    reload: "重新整理",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `我們將這部裝置命名為「${name}」。`,
    cardLooksGood: "可以",
    cardEdit: "修改",
    cardAriaLabel: "確認裝置名稱",
    renameTitle: "重新命名這部裝置",
    renameLabel: "裝置名稱",
    renameSave: "儲存",
    renameCancel: "取消",
    renameFailed: "重新命名失敗。請再試一次。",
  },

  collision: {
    title: (name: string): string => `你已經有一部「${name}」`,
    subtitle: "要怎麼稱呼這一部？",
    chipWork: (name: string): string => `公司 ${name}`,
    chipHome: (name: string): string => `家用 ${name}`,
    chipPersonal: (name: string): string => `個人 ${name}`,
    customLabel: "自訂名稱",
    save: "儲存",
    useAnyway: "仍使用原本的名稱",
    saveFailed: "新名稱儲存失敗。請再試一次。",
    ariaLabel: "解決重複的裝置名稱",
  },

  clipboard: {
    title: "剪貼簿",
    emptyTitle: "尚無剪貼項目",
    empty: "其他裝置上的剪貼項目會顯示在這裡。點一下即可複製。",
    from: (label: string): string => `來自 ${label}`,
    to: (label: string): string => `發往 ${label}`,
    copy: "複製到剪貼簿",
    copied: "已複製",
    reveal: "顯示",
    hide: "隱藏",
    sensitiveChip: "敏感內容",
    cloudChip: "來自你的 API 權杖",
    maskedHint: "內容已隱藏——按「顯示」即可查看",
    delete: "刪除剪貼項目",
    sendPlaceholder: "貼上要傳送的文字…",
    sendCta: "傳送",
    sendSensitiveToggle: "標示為敏感內容",
    sendSuccess: (n: number): string =>
      `已傳送到 ${n} ${selectPlural("zh-TW", n, { other: "部裝置" })}。`,
    sendPartial: (n: number, total: number): string =>
      `已傳送到 ${total} ${selectPlural("zh-TW", total, { other: "部裝置" })}中的 ${n} 部。`,
    sendNoDevices: "沒有可接收的已配對裝置。",
    sendFailed: "傳送失敗。請再試一次。",
    sendEmpty: "沒有可傳送的內容。",
    dockRecentHeading: "最近",
    dockEmptyClipboard: "剪貼簿是空的。",
    clearAll: "全部清除",
    clearAllConfirm: "確定清除全部",
    clearAllCancel: "取消",
    shareTitle: "傳送到我的裝置",
    shareReview: "確認要傳送的內容。它會經端對端加密傳送到你的已配對裝置。",
    shareSend: "傳送",
    shareCancel: "取消",
    shareNotPaired: "要傳送分享的文字，請先配對一部裝置。",
    shareOpenApp: "開啟 Reflecto",
    pasteFromClipboard: "從剪貼簿貼上",
    readFailed: "無法讀取剪貼簿，請在下方貼上。",
  },
  install: {
    title: "安裝 Reflecto",
    body: "加入主畫面，存取更快速。",
    cta: "安裝",
    dismissAria: "關閉安裝提示",
  },
  nav: {
    home: "首頁",
    clipboard: "剪貼簿",
    files: "檔案",
    ariaLabel: "主要導覽",
    fileHiddenPublicMode: "隱私模式開啟時隱藏。",
  },
  common: {
    loading: "載入中",
  },
  errors: {
    notPaired: "未配對",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${os} 上的 ${browser}`,
    webFallback: "網頁瀏覽器",
  },
  palette: {
    placeholder: "輸入指令…",
    noResults: "沒有符合的指令",
    ariaLabel: "指令面板",
    clearAll: "清除所有通知",
    glance: "Glance 摘要",
    cancelSnooze: "取消暫停",
    copyClip: "複製最近收到的剪貼項目",
  },

  fileTransfer: {
    sendTitle: "傳送檔案",
    sendChooseFile: "選擇檔案",
    sendDeviceLabel: "傳送到",
    sendPhoneFallbackName: "手機",
    send: "傳送檔案",
    sendPreparing: (pct: number | null): string =>
      pct === null ? "正在準備…" : `正在準備——${pct}%`,
    sendDelivering: "正在傳送到手機…",
    sendWaitingAccept: "正在等待手機接受…",
    sendWaitingAppOpen: "在手機上開啟 Reflecto 以接受",
    sendWaitingAppOpenHint: "你的手機無法接收推播通知，因此只有開啟 Reflecto 後才會顯示這個檔案。",
    sendUploading: (partNumber: number, partCount: number): string =>
      `正在上傳第 ${partNumber}/${partCount} 部分…`,
    sendDone: "已傳送。",
    sendFailedRejected: "手機拒收了這個檔案。",
    sendFailedNoAnswer: "手機沒有回應。",
    sendFailedUploadFailed: "上傳失敗。請再試一次。",
    sendFailedCancelled: "已取消。",
    sendFailedGeneric: "發生問題。請再試一次。",
    sendTooLarge: (maxMiB: number): string => `檔案太大（上限 ${maxMiB} MiB）。`,
    sendKeepOpenHint: "在傳輸完成前，請讓這個分頁保持開啟。",
    sendAnother: "再傳一個",
    sendNoDevice: "要傳送檔案，請先配對一部手機。",
    sendCancel: "取消",
    sendPeerProgress: (chunksDone: number): string => `手機已接收 ${chunksDone} 個資料區塊。`,

    receiveTitle: "傳入的檔案",
    receiveOfferFrom: (sender: string): string => `${sender} 想傳送一個檔案給你`,
    receiveAccept: "接受",
    receiveDecline: "拒絕",
    receiveReceiving: (n: number, total: number): string => `正在接收第 ${n}/${total} 部分…`,
    receiveDone: "已儲存。",
    receiveFailed: "傳輸失敗。",
    receiveCancelled: "已取消。",
    receiveCancel: "取消",

    errorChunkAuthFail: "傳輸失敗——無法驗證資料。請再試一次。",
    errorHashMismatch: "傳輸失敗——檔案未通過完整性檢查。請再試一次。",
    errorStorageFull: "儲存空間不足，無法完成這次傳輸。",
    errorExpired: "傳輸已過期。",
    errorQuotaExceeded: "已達每日傳輸上限——請明天再試。",
    errorInternal: "傳輸失敗——發生問題。",
    /** Status while bytes move. Derived from completed ~5 MiB parts, so it steps coarsely. */
    transferringPct: (pct: number): string => `${pct}%`,
    /** aria-label for the x on a FINISHED row — clears it, does not stop anything. */
    dismiss: "關閉",
    /** aria-label for the progress bar; a bare percentage announces nothing useful alone. */
    progressLabel: "傳輸進度",
    outgoing: (n: number): string => `傳送中 (${n})`,
    incoming: (n: number): string => `接收中 (${n})`,
    /** Bulk action; only shown when more than one offer is still pending. */
    acceptAll: "全部接受",
    dropTitle: "拖放到這裡",
    /** `size` is pre-formatted and derived from the protocol ceiling at runtime — never
     *  hardcode a number here, the server can tighten the cap. */
    dropLimit: (size: string): string => `最大 ${size} 的檔案。`,
    /** Must contain dropTitle verbatim: WCAG 2.5.3 (Label in Name). */
    dropAriaLabel: "拖放到這裡，或選擇要傳送的檔案",
    /** Chip on a transfer row: bytes travel device-to-device, not via the relay. */
    transportDirect: "直連",
    transportDirectHint: "裝置間直接傳送 — 從不儲存在我們的伺服器上。",
    /** Chip for the relay route. BOTH routes are end-to-end encrypted — this is a
     *  route/speed distinction, never a security one, so the copy must not alarm. */
    transportRelay: "經伺服器",
    transportRelayHint: "經我們的伺服器轉送，仍為端對端加密 — 我們無法讀取。",
    directBadge: "直連",
  },
  time: {
    justNow: "剛剛",
    minutesAgo: (count: number): string => `${count} 分鐘前`,
    hoursAgo: (count: number): string => `${count} 小時前`,
  },
} satisfies MessagesShape;
